import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { SupportedCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { RangeInput } from '@sorare/core/src/atoms/inputs/RangeInput';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  HeadlineL,
  LabelL,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import {
  FOOTBALL_MARKET,
  FOOTBALL_PLAY_PRO_ONBOARDING_CLUB_SELECTION,
} from '@sorare/core/src/constants/__generated__/routes';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useTransitionMemo } from '@sorare/core/src/hooks/ui/useTransitionMemo';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import lineupStackCommon from './assets/lineup-stack-common.png';
import lineupStackLimited from './assets/lineup-stack-limited.png';
import lineupStackRare from './assets/lineup-stack-rare.png';

const StyledContainer = styled(Container)`
  background: linear-gradient(to bottom, #2c2b70 0%, rgba(44, 43, 112, 0) 100%);
`;
const Wrapper = styled(Vertical)`
  height: var(--100vh);
  align-items: center;
  padding: var(--double-unit) 0;
  @media ${tabletAndAbove} {
    padding: var(--quadruple-unit) 0;
    gap: calc(6 * (var(--unit)));
  }
`;

const ImgWrapper = styled(Vertical)`
  position: relative;
  overflow: auto;
  & > img {
    width: 100%;
    flex: 1;
    object-fit: contain;
  }
  flex: 1;
  @media ${tabletAndAbove} {
    & > img {
      width: unset;
      max-height: 100%;
      max-width: 100%;
    }
  }
`;

const Footer = styled(Vertical)`
  align-self: stretch;
  @media ${tabletAndAbove} {
    align-items: center;
  }
`;

const InputBlock = styled(Vertical)`
  width: 100%;
  @media ${tabletAndAbove} {
    align-self: center;
    width: min(80vw, 800px);
  }
`;

const LineupCostWrapper = styled(Vertical).attrs({ center: true })`
  position: absolute;
  inset: 0;
`;
const LineupCost = styled.div`
  border: 1px solid var(--c-white);
  backdrop-filter: blur(10px);
  background: var(--c-nd-50);
  border-radius: var(--quadruple-unit);
  padding: var(--double-unit);
`;

const StyledRangeInput = styled(RangeInput)`
  --track-bg: linear-gradient(
    92.13deg,
    #ffffff 0%,
    #eabfe2 28.88%,
    #ffffff 48.67%,
    #bedecb 64.58%,
    #ffffff 100%
  );
`;

const ASSETS_MAP = {
  common: lineupStackCommon,
  limited: lineupStackLimited,
  rare: lineupStackRare,
};
const COSTS_MAP = {
  common: 0,
  limited: 80,
  rare: 160,
};

const ChooseYourLevelPage = () => {
  const [inputValue, setInputValue] = useState(0);
  const isTabletAndAbove = useIsTabletAndAbove();

  const levelValue = useTransitionMemo(() => {
    if (inputValue <= 5000) {
      return 'common';
    }
    if (inputValue <= 15000) {
      return 'limited';
    }
    return 'rare';
  }, [inputValue]);

  const onInputRelease = () => {
    setInputValue(prevValue => {
      if (prevValue <= 5000) {
        return 0;
      }
      if (prevValue <= 15000) {
        return 10000;
      }
      return 20000;
    });
  };

  const { main } = useAmountWithConversion({
    monetaryAmount: {
      usd: COSTS_MAP[levelValue] * 100,
      referenceCurrency: SupportedCurrency.USD,
    },
    hideFractionDigits: true,
  });
  return (
    <StyledContainer>
      <Wrapper>
        <Vertical center>
          <HeadlineL>
            <FormattedMessage
              id="ChooseYourLevelPage.title"
              defaultMessage="Choose your level"
            />
          </HeadlineL>
          <BodyM color="var(--c-nd-600)">
            <FormattedMessage
              id="ChooseYourLevelPage.description"
              defaultMessage="Don’t worry, you can switch anytime!"
            />
          </BodyM>
        </Vertical>
        <ImgWrapper>
          <img src={ASSETS_MAP[levelValue]} alt="" width="665" height="727" />
          <LineupCostWrapper>
            <LineupCost>
              <LabelL>
                <FormattedMessage
                  id="ChooseYourLevelPage.lineupCost"
                  defaultMessage="Avg. lineup cost: {cost}"
                  values={{
                    cost: main,
                  }}
                />
              </LabelL>
            </LineupCost>
          </LineupCostWrapper>
        </ImgWrapper>
        <Footer gap={2}>
          <InputBlock gap={3}>
            <SBHorizontal>
              <LabelM color="var(--c-nd-600)">
                <FormattedMessage
                  id="ChooseYourLevelPage.basicRewards"
                  defaultMessage="Basic rewards"
                />
              </LabelM>
              <LabelM color="var(--c-nd-600)">
                <FormattedMessage
                  id="ChooseYourLevelPage.premiumRewards"
                  defaultMessage="Premium rewards"
                />
              </LabelM>
            </SBHorizontal>
            <StyledRangeInput
              value={inputValue}
              min="0"
              max="20000"
              step="1"
              onChange={e => {
                e.stopPropagation();
                setInputValue(e.target.valueAsNumber);
              }}
              onRelease={onInputRelease}
            />
            <SBHorizontal>
              <Vertical
                gap={0}
                center
                as="button"
                onClick={() => setInputValue(0)}
              >
                <LabelL>
                  <FormattedMessage {...glossary.amateur} />
                </LabelL>
                <LabelL color="var(--c-nd-600)">
                  <FormattedMessage {...glossary.free} />
                </LabelL>
              </Vertical>
              <Vertical
                gap={0}
                center
                as="button"
                onClick={() => setInputValue(10000)}
              >
                <Horizontal gap={0.5}>
                  <LabelL>
                    <FormattedMessage {...glossary.pro} />
                  </LabelL>
                  <LabelL color="var(--c-nd-600)">Limited</LabelL>
                </Horizontal>
                <LabelL color="var(--c-nd-600)">$</LabelL>
              </Vertical>
              <Vertical
                gap={0}
                center
                as="button"
                onClick={() => setInputValue(20000)}
              >
                <Horizontal gap={0.5}>
                  <LabelL>
                    <FormattedMessage {...glossary.pro} />
                  </LabelL>
                  <LabelL color="var(--c-nd-600)">Rare</LabelL>
                </Horizontal>
                <LabelL color="var(--c-rivals-win)">$$</LabelL>
              </Vertical>
            </SBHorizontal>
          </InputBlock>
          <Button
            size="large"
            color="secondary"
            to={
              levelValue === 'common'
                ? FOOTBALL_PLAY_PRO_ONBOARDING_CLUB_SELECTION
                : FOOTBALL_MARKET
            }
            fullWidth={!isTabletAndAbove}
          >
            <FormattedMessage {...glossary.continue} />
          </Button>
        </Footer>
      </Wrapper>
    </StyledContainer>
  );
};
export default ChooseYourLevelPage;
